/**
 * WaiveModal — Phase 10 Task 10.5
 *
 * Modal for waiving an engine review with a written reason. Replaces
 * the prior `window.prompt('Reason for waiving …')` shortcut in
 * CalculationReviewPanel.handleWaive — that path was tagged "MVP" by
 * the original author and produced a free-text capture surface with no
 * privilege guardrail. Phase 10 closes that loophole by wrapping the
 * reason input in <CpaWorkpaperTextarea>, which renders the canonical
 * non-dismissible privilege banner above the field.
 *
 * Backend contract: POST /api/v1/external-accountant/{report_id}/{engine_id}/waive
 * with WaiveEngineRequest body. Reason min length is 30 chars (matches
 * the prior window.prompt validation).
 */

import React, { useState } from 'react';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/Button';
import CpaWorkpaperTextarea from '../../privilege/CpaWorkpaperTextarea';
import type { EngineId, WaivePayload } from '../../../types/engineReview';
import { ENGINE_NAMES } from '../../../types/engineReview';

const REASON_MIN_LENGTH = 30;

export interface WaiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: WaivePayload) => void;
  engineId: EngineId;
  isSubmitting?: boolean;
}

export default function WaiveModal({
  isOpen,
  onClose,
  onSubmit,
  engineId,
  isSubmitting = false,
}: WaiveModalProps) {
  const [reason, setReason] = useState('');

  const engineName = ENGINE_NAMES[engineId];
  const reasonValid = reason.trim().length >= REASON_MIN_LENGTH;
  const canSubmit = reasonValid;

  const handleSubmit = () => {
    onSubmit({
      reason: reason.trim(),
      override_warnings: [],
    });
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Waive ${engineName}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="waive-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            data-testid="waive-modal-submit"
          >
            {isSubmitting ? 'Submitting...' : 'Confirm Waive'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="waive-modal">
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
            Waiving this engine excludes it from the readiness gate. The waiver
            is recorded in the attestation audit chain. Provide a written
            justification a CPA peer would find sufficient.
          </p>
        </div>

        <CpaWorkpaperTextarea
          id="waive-reason"
          data-testid="waive-modal-reason"
          label={
            <>
              Reason for Waiver <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
            </>
          }
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={`Explain why this engine should be waived (minimum ${REASON_MIN_LENGTH} characters)...`}
          rows={5}
          className="w-full px-3 py-2 border rounded-lg text-sm"
          style={
            reason.length > 0 && !reasonValid
              ? { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }
              : { borderColor: 'var(--color-border)' }
          }
          helperText={
            <>
              {reason.trim().length}/{REASON_MIN_LENGTH} characters minimum
              {reason.length > 0 && !reasonValid && (
                <span className="ml-2" style={{ color: 'var(--color-needs-human-text)' }}>
                  - Please provide more detail
                </span>
              )}
            </>
          }
        />
      </div>
    </Modal>
  );
}

export { WaiveModal };
